import React, { useState, useEffect } from 'react';
import axiosInstance from '../axiosConfig';
import { useParams, Link } from 'react-router-dom';
import Header from '../Components/Header';
import Sidebar from '../Components/Sidebar';
import '../styles.css';

const LeaveDetails = () => {
    const { id } = useParams();
    const [userData, setUserData] = useState(null);
    const [leaveRequest, setLeaveRequest] = useState(null);
    const [loading, setLoading] = useState(true);
    const [cancelled, setCancelled] = useState(false);
    const [error, setError] = useState(null);

    const currentUserId = localStorage.getItem('id');
    const [isSidebarVisible, setIsSidebarVisible] = useState(false);
    const [isIdCardPopupVisible, setIsIdCardPopupVisible] = useState(false);

    useEffect(() => {
        const screenWidth = window.innerWidth;
        if (screenWidth < 520) {
            setIsSidebarVisible(false);
        } else {
            setIsSidebarVisible(true);
        }
    }, []);

    const handleToggleClick = () => {
        setIsSidebarVisible(!isSidebarVisible);
    };

    useEffect(() => {
        if (currentUserId) {
            axiosInstance.get(`/user/${currentUserId}/`)
                .then((response) => {
                    setUserData(response.data);
                    return axiosInstance.get(`/get-leave-request/${id}/`);
                })
                .then((response) => {
                    setLeaveRequest(response.data);
                    setLoading(false);
                })
                .catch((error) => {
                    console.error('Error fetching data:', error);
                    setError('Failed to load leave details');
                    setLoading(false);
                });
        }
    }, [currentUserId, id]);

    const handleCancelRequest = () => {
        axiosInstance.post(`/cancel-leave-request/${leaveRequest.id}/`)
            .then((response) => {
                console.log('Leave request cancelled successfully');
                setCancelled(true);
                setLeaveRequest({
                    ...leaveRequest,
                    status: 'cancelled'
                });
            })
            .catch((error) => {
                console.error('Error cancelling leave request:', error);
                setError('Failed to cancel leave request');
            });
    };

    const handleLogout = () => {
        localStorage.clear();
        window.location.href = '/';
    };

    const profilePhotoUrl = userData?.profile_photo?.startsWith('http://')
        ? userData?.profile_photo.replace('http://', 'https://')
        : userData?.profile_photo;

    const menuItems = [
        { icon: '🏠', label: 'Dashboard', path: '/user-dashboard' },
        { icon: '👤', label: 'My Profile', path: `/user-profile/${userData?.id}` },
        { icon: '📅', label: 'Attendance', path: `/leave-attendance/${userData?.id}` },
        { icon: '📋', label: 'Leave Management', path: `/leave-management/${userData?.id}` },
        { icon: '✏️', label: 'Create Leave Request', path: `/leave-request/${userData?.id}` },
        { icon: '💰', label: 'Leave Balance', path: '/leave-balance' },
        { icon: '📊', label: 'Leave Transactions', path: '/leave-transaction/' },
        // { icon: '📅', label: 'Calendar', path: '/calendar' },
        // { icon: '&#128178;', label: 'Payroll', path: 'https://hrms.teamhalt.com/', active: false },
        { icon: '🆔', label: 'ID Card', action: () => setIsIdCardPopupVisible(true) },
    ];

    const getStatusBadgeClass = (status) => {
        const statusLower = status?.toLowerCase();
        return `status-badge ${statusLower}`;
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleDateString('en-GB').replace(/\//g, '-');
    };

    const formatDateTime = (dateString) => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleString();
    };

    return (
        <div className={`dashboard-wrapper ${isSidebarVisible ? 'sidebar-open' : 'sidebar-closed'}`}>
            <Header
                onToggleClick={handleToggleClick}
                isSidebarVisible={isSidebarVisible}
            />

            <Sidebar
                isSidebarVisible={isSidebarVisible}
                currentUser={userData}
                menuItems={menuItems}
                handleLogout={handleLogout}
            />

            <div className="dashboard-main-content">
                <div className="leave-details-container">
                    <div className="leave-details-header">
                        <div className="header-row">
                            <Link to="/leave-transaction/" className="back-link">
                                <span>←</span> Back to Transactions
                            </Link>
                        </div>
                        <h1>Leave Request Details</h1>
                        <p>View your leave request information</p>
                    </div>

                    {loading ? (
                        <div className="leave-details-loading">
                            <div className="loading-spinner"></div>
                            <p>Loading leave details...</p>
                        </div>
                    ) : error ? (
                        <div className="leave-details-error">
                            <p>{error}</p>
                        </div>
                    ) : leaveRequest ? (
                        <div className="leave-details-card">
                            <div className="leave-details-content">
                                <div className="details-section">
                                    <h3>Request Information</h3>
                                    <div className="details-grid">
                                        <div className="detail-item">
                                            <span className="detail-label">Applied On</span>
                                            <span className="detail-value">{formatDateTime(leaveRequest.created_at)}</span>
                                        </div>
                                        <div className="detail-item">
                                            <span className="detail-label">Leave Type</span>
                                            <span className="detail-value">{leaveRequest.leave_type}</span>
                                        </div>
                                        <div className="detail-item">
                                            <span className="detail-label">Start Date</span>
                                            <span className="detail-value">{formatDate(leaveRequest.start_date)}</span>
                                        </div>
                                        <div className="detail-item">
                                            <span className="detail-label">End Date</span>
                                            <span className="detail-value">{formatDate(leaveRequest.end_date)}</span>
                                        </div>
                                        <div className="detail-item">
                                            <span className="detail-label">Duration</span>
                                            <span className="detail-value">{leaveRequest.leave_duration}</span>
                                        </div>
                                        <div className="detail-item">
                                            <span className="detail-label">Status</span>
                                            <span className={getStatusBadgeClass(leaveRequest.status)}>
                                                {leaveRequest.status}
                                            </span>
                                        </div>
                                        <div className="detail-item">
                                            <span className="detail-label">Updated At</span>
                                            <span className="detail-value">{formatDateTime(leaveRequest.updated_at)}</span>
                                        </div>
                                    </div>
                                </div>

                                <div className="details-section">
                                    <h3>Reason</h3>
                                    <p className="reason-text">{leaveRequest.reason || 'No reason provided'}</p>
                                </div>

                                {cancelled ? (
                                    <div className="cancel-success-message">
                                        <span>✓</span> Request Cancelled Successfully!
                                    </div>
                                ) : (
                                    leaveRequest.status?.toLowerCase() === 'pending' && (
                                        <div className="action-section">
                                            <button 
                                                className="cancel-request-btn" 
                                                onClick={handleCancelRequest}
                                            >
                                                Cancel Request
                                            </button>
                                        </div>
                                    )
                                )}
                            </div>
                        </div>
                    ) : (
                        <div className="leave-details-empty">
                            <p>No leave request found.</p>
                        </div>
                    )}
                </div>

                {isIdCardPopupVisible && (
                    <div className="id-card-overlay" onClick={() => setIsIdCardPopupVisible(false)}>
                        <div className="id-card-popup" onClick={(e) => e.stopPropagation()}>
                            <button className="id-card-close" onClick={() => setIsIdCardPopupVisible(false)}>×</button>
                            <div className="id-card-header">
                                <img src="/Teamhalt.png" alt="Logo" className="id-card-logo" />
                                <span>TEAMHALT PRIVATE LIMITED</span>
                            </div>
                            <div className="id-card-body">
                                <div className="id-card-profile">
                                    <img src={profilePhotoUrl || '/profile.jpg'} alt="Profile" className="id-card-photo" />
                                    <div className="id-card-info">
                                        <h4>{userData?.first_name} {userData?.last_name}</h4>
                                        <p className="id-designation">{userData?.designation}</p>
                                        <div className="id-dob">
                                            <strong>DOB:</strong>
                                            <span>{userData?.date_of_birth ? formatDate(userData.date_of_birth) : "N/A"}</span>
                                        </div>
                                    </div>
                                </div>
                                <hr className="id-card-divider" />
                                <div className="id-card-details">
                                    {[
                                        { label: "Emp ID", value: userData?.employee_code },
                                        { label: "Department", value: userData?.department },
                                        { label: "ESIC No.", value: userData?.esic_no },
                                        { label: "UAN No.", value: userData?.uan_no },
                                        { label: "Gender", value: userData?.gender },
                                        { label: "Contact No.", value: userData?.contact_number },
                                    ].map((item, idx) => (
                                        <div key={idx} className="detail-row">
                                            <strong>{item.label}:</strong>
                                            <span>{item.value || "N/A"}</span>
                                        </div>
                                    ))}
                                </div>
                                {typeof userData?.work_location_name === "string" && userData.work_location_name.toLowerCase() !== "noida" && (
                                    <div className="id-card-duty">
                                        <strong>On Contractual Duty Of:</strong>
                                        <span>{userData?.duty_of || "N/A"}</span>
                                    </div>
                                )}
                                <div className="id-card-footer">https://teamhalt.com/</div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default LeaveDetails;

